import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { NotificationsService } from 'angular2-notifications';
import {
  EMPTY,
  Subject,
  catchError,
  finalize,
  switchMap,
  take,
  takeUntil,
  throwError,
} from 'rxjs';
import { VehicleApiService } from 'src/app/core/services/api/vehicle-api.service';
import { ApiResponse } from 'src/app/shared/models/api-response.interface';
import { ThirdParty } from 'src/app/shared/models/third-party.interface';
import {
  VINValidator,
  YearValidator,
} from 'src/app/shared/utils/validators.util';
import {
  VehicleBrand,
  VehicleCreationData,
  VehicleFromVIN,
  VehicleModel,
  VehicleModelsList,
  VehicleType,
} from 'src/app/shared/models/vehicle.model';
import { VehiclesLogicService } from '../../../modules/vehicles/services/vehicles-logic.service';
import { InputFieldType } from 'src/app/shared/models/input-types.enum';
import { ActivatedRoute, Params, Router } from '@angular/router';

@Component({
  selector: 'app-vehicle-form',
  templateUrl: './vehicle-form.component.html',
  styleUrls: ['./vehicle-form.component.scss'],
})
export class VehicleFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isInsideContract: boolean = false;
  @Input() doNotTakeIdFromUrl: boolean = false;
  @Input() isModalFooter: boolean = false;
  @Input() prefillVin: string = '';
  @Output() onCancelCreation = new EventEmitter<void>();
  @Output() onSubmit = new EventEmitter<VehicleCreationData>();
  isLoading: boolean = false;
  vehicleForm!: FormGroup;
  vehicleTypes: VehicleType[] = [];
  classOptions: any[] = [];
  brandOptions: VehicleBrand[] = [];
  modelsOptions: VehicleModel[] = [];
  yearsOptions: { value: string }[] = [];
  ownerOptions: ThirdParty[] = [];
  isVINNumberValid: boolean = false;
  isClassOptionsLoading: boolean = false;
  isBrandsLoading: boolean = false;
  isModelsLoading: boolean = false;
  isYearsLoading: boolean = false;
  editedVehicleId?: number;

  // VIN Decode Errors
  vinDecodeErrorMessage: string = '';
  vinDecodeAdditionalError: string = '';
  vinDecodeSuggestedVin: string = '';
  isVinDecodeFailed: boolean = false;

  private readonly destroy$ = new Subject<void>();
  protected readonly InputFieldType = InputFieldType;

  constructor(
    private vehicleService: VehicleApiService,
    private toastService: NotificationsService,
    private vehicleLogicService: VehiclesLogicService,
    private notificationService: NotificationsService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.applyPrefilledVin();
    this.fetchAdditionalData();

    // Subscribe to manual input switch changes
    this.vehicleForm.get('manual_input_switch')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => {
        this.onManualInputToggle(value);
      });

    if (!this.doNotTakeIdFromUrl) {
      this.checkRouteParam();
    }
  }

  checkRouteParam() {
    this.isLoading = true;
    this.route.params
      .pipe(
        take(1),
        switchMap((params: Params) => {
          this.editedVehicleId = Number(params?.['id']);
          if (!!this.editedVehicleId) {
            return this.vehicleService.getById(this.editedVehicleId);
          } else {
            return EMPTY;
          }
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe((result?: ApiResponse) => {
        const vehicle = result?.data;
        this.handleEditedVehicle(vehicle);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['prefillVin'] && this.vehicleForm) {
      this.applyPrefilledVin();
    }
  }

  handleCreationCancel(): void {
    this.onCancelCreation.emit();
  }

  handleSubmit(): void {
    const formValue = this.vehicleForm.value;
    try {
      const vehicle = this.vehicleLogicService.getCreationData(
        formValue,
        this.isVINNumberValid,
      );
      if (this.isInsideContract) {
        this.onSubmit.emit(vehicle);
      } else {
        this.isLoading = true;

        let request$;
        if (formValue.manual_input_switch) {
          request$ = this.vehicleService.storeManualVehicle(vehicle);
        } else {
          request$ = !!this.editedVehicleId
            ? this.vehicleService.update(this.editedVehicleId, vehicle)
            : this.vehicleService.create(vehicle);
        }

        request$
          .pipe(
            take(1),
            catchError(({ error }: any) => {
              this.notificationService.error('Error', error.message);
              Object.values(error.error).forEach((errorField: unknown) => {
                if (Array.isArray(errorField)) {
                  errorField.forEach((errorMessage: string) =>
                    this.notificationService.error('Error', errorMessage),
                  );
                }
              });
              return throwError(() => error.message);
            }),
            finalize(() => (this.isLoading = false)),
          )
          .subscribe((result: ApiResponse) => {
            this.notificationService.success('Success', result.message);
            this.router.navigate(['/dashboard/vehicles']);
          });
      }
    } catch {
      this.vehicleForm.markAllAsTouched();
      this.notificationService.error(
        'Error',
        'Please ensure the form is filled correctly',
      );
    }
  }

  validateVin(): void {
    const formValue = this.vehicleForm.value;

    // Clear previous errors
    this.vinDecodeErrorMessage = '';
    this.vinDecodeAdditionalError = '';
    this.vinDecodeSuggestedVin = '';
    this.isVinDecodeFailed = false;

    if (!formValue.type?.id || !formValue.vin) {
      this.toastService.error(
        'Error',
        'Please enter vehicle type and VIN number',
      );
      return;
    } else {
      this.resetVehicleData();
      this.isVINNumberValid = false;
      this.isLoading = true;
      const formValue = this.vehicleForm.value;
      this.vehicleService
        .getByVIN(formValue.type.id, formValue.vin)
        .pipe(
          take(1),
          finalize(() => (this.isLoading = false)),
        )
        .subscribe({
          next: (result: ApiResponse) => {
            const vehicle: VehicleFromVIN = result.data;
            if (vehicle) {
              this.handleVehicleFormVINResponse(vehicle);

              if (vehicle.error_message && !this.isCleanVinDecodeMessage(vehicle.error_message)) {
                this.vinDecodeErrorMessage = vehicle.error_message;
              }
              if (
                vehicle.additional_error &&
                !this.isCleanVinDecodeAdditionalMessage(vehicle.additional_error, vehicle.error_message)
              ) {
                this.vinDecodeAdditionalError = vehicle.additional_error;
              }
              if (vehicle.suggested_vin) {
                this.vinDecodeSuggestedVin = vehicle.suggested_vin;
              }
            }
          },
          error: () => {
            this.isVinDecodeFailed = true;
            this.vehicleForm.get('manual_input_switch')?.setValue(false, { emitEvent: false });
          }
        });
    }
  }

  searchOwners(searchEvent: { query: string }): void {
    this.vehicleService
      .getOwners(searchEvent.query)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: ApiResponse) => {
        if (result.data?.length) {
          this.ownerOptions = result.data;
        }
      });
  }

  handleTypeSelect(selectionEvent: { value: VehicleType }): void {
    // Reset manual input switch and clear all Make/Model/Year fields
    this.vehicleForm.patchValue({
      manual_input_switch: false,
      make: null,
      model: null,
      year: null,
      make_text: null,
      model_text: null,
      year_text: null,
    }, { emitEvent: false });

    // Clear options
    this.modelsOptions = [];
    this.yearsOptions = [];

    // Restore dropdown validators
    this.vehicleForm.get('make')?.setValidators([Validators.required]);
    this.vehicleForm.get('model')?.setValidators([Validators.required]);
    this.vehicleForm.get('year')?.setValidators([Validators.required]);
    this.vehicleForm.get('make_text')?.clearValidators();
    this.vehicleForm.get('model_text')?.clearValidators();
    this.vehicleForm.get('year_text')?.clearValidators();

    this.vehicleForm.get('make')?.updateValueAndValidity({ emitEvent: false });
    this.vehicleForm.get('model')?.updateValueAndValidity({ emitEvent: false });
    this.vehicleForm.get('year')?.updateValueAndValidity({ emitEvent: false });

    this.fetchBrands(selectionEvent.value.id);
  }

  handleBrandSelect(selectionEvent: { value: VehicleModelsList }): void {
    // Clear year options and reset year fields
    this.yearsOptions = [];
    this.vehicleForm.patchValue({
      model: null,
      year: null,
    }, { emitEvent: false });

    this.vehicleForm.get('model')?.updateValueAndValidity({ emitEvent: false });
    this.vehicleForm.get('year')?.updateValueAndValidity({ emitEvent: false });

    const isManualMode = this.vehicleForm.get('manual_input_switch')?.value;
    if (!isManualMode) {
      this.fetchModels(selectionEvent.value.id);
    } else {
      this.modelsOptions = [];
    }
  }

  handleModelSelect(selectionEvent: { value: VehicleModel }): void {
    this.vehicleForm.patchValue({
      year: null,
    }, { emitEvent: false });

    this.vehicleForm.get('year')?.updateValueAndValidity({ emitEvent: false });

    const isManualMode = this.vehicleForm.get('manual_input_switch')?.value;
    if (!isManualMode) {
      this.fetchYears(selectionEvent.value.id as number);
    } else {
      this.yearsOptions = [];
    }
  }

  handleColorInput(inputEvent: Event): void {
    if (!inputEvent?.target || !(inputEvent?.target as HTMLInputElement)?.value)
      return;
    this.vehicleForm.patchValue({
      vehicle_color: (inputEvent?.target as HTMLInputElement).value,
    });
  }

  onManualInputToggle(value: boolean): void {
    if (value) {
      // Switching to manual mode
      this.vehicleForm.patchValue({
        make: null,
        model: null,
        year: null,
      }, { emitEvent: false });

      // Clear dropdown validators
      this.vehicleForm.get('make')?.clearValidators();
      this.vehicleForm.get('model')?.clearValidators();
      this.vehicleForm.get('year')?.clearValidators();

      // Add text input validators
      this.vehicleForm.get('make_text')?.setValidators([Validators.required]);
      this.vehicleForm.get('model_text')?.setValidators([Validators.required]);
      this.vehicleForm.get('year_text')?.setValidators([
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(4),
        YearValidator
      ]);

      // Remove VIN validation for manual mode (keep required)
      this.vehicleForm.get('vin')?.setValidators([Validators.required]);

      // Clear options arrays
      this.modelsOptions = [];
      this.yearsOptions = [];
    } else {
      // Switching to dropdown mode
      this.vehicleForm.patchValue({
        make_text: null,
        model_text: null,
        year_text: null,
      }, { emitEvent: false });

      // Add dropdown validators
      this.vehicleForm.get('make')?.setValidators([Validators.required]);
      this.vehicleForm.get('model')?.setValidators([Validators.required]);
      this.vehicleForm.get('year')?.setValidators([Validators.required]);

      // Clear text input validators
      this.vehicleForm.get('make_text')?.clearValidators();
      this.vehicleForm.get('model_text')?.clearValidators();
      this.vehicleForm.get('year_text')?.clearValidators();

      // Restore VIN validation for dropdown mode
      this.vehicleForm.get('vin')?.setValidators([Validators.required, VINValidator]);
    }

    // Update validity for all affected controls
    this.vehicleForm.get('make')?.updateValueAndValidity({ emitEvent: false });
    this.vehicleForm.get('model')?.updateValueAndValidity({ emitEvent: false });
    this.vehicleForm.get('year')?.updateValueAndValidity({ emitEvent: false });
    this.vehicleForm.get('make_text')?.updateValueAndValidity({ emitEvent: false });
    this.vehicleForm.get('model_text')?.updateValueAndValidity({ emitEvent: false });
    this.vehicleForm.get('year_text')?.updateValueAndValidity({ emitEvent: false });
    this.vehicleForm.get('vin')?.updateValueAndValidity({ emitEvent: false });
  }

  private initForm(): void {
    this.vehicleForm = this.vehicleLogicService.buildForm();
  }

  private isCleanVinDecodeMessage(message: string): boolean {
    return message.toLowerCase().includes('vin decoded clean');
  }

  private isCleanVinDecodeAdditionalMessage(additionalMessage: string, errorMessage?: string): boolean {
    const additional = additionalMessage.toLowerCase();
    return this.isCleanVinDecodeMessage(errorMessage || '') &&
      additional.includes('incomplete vehicle warning');
  }

  private applyPrefilledVin(): void {
    const vin = this.prefillVin?.trim();
    if (!vin || this.editedVehicleId) {
      return;
    }

    this.vehicleForm.get('vin')?.patchValue(vin);
    this.vehicleForm.get('vin')?.markAsDirty();
    this.vehicleForm.get('vin')?.updateValueAndValidity();
  }

  private fetchAdditionalData(): void {
    this.fetchVehicleTypes();
    this.fetchClasses();
  }

  private fetchVehicleTypes(): void {
    this.vehicleService
      .getTypes()
      .pipe(take(1))
      .subscribe((response: ApiResponse) => {
        if (response.data) {
          this.vehicleTypes = response.data;
          const passengerCarType = this.vehicleTypes?.find(
            (t) => t.name === 'Passenger Car',
          );
          const vehicleTypeControl = this.vehicleForm?.get('type');
          if (
            passengerCarType &&
            !this.editedVehicleId &&
            !vehicleTypeControl?.value
          ) {
            vehicleTypeControl?.patchValue(passengerCarType);
            this.handleTypeSelect({ value: passengerCarType });
          }
        }
      });
  }

  private fetchClasses(): void {
    this.isClassOptionsLoading = true;
    this.vehicleService
      .getClasses()
      .pipe(
        take(1),
        finalize(() => (this.isClassOptionsLoading = false)),
      )
      .subscribe((response: ApiResponse) => {
        if (response.data) {
          this.classOptions = response.data;
        }
      });
  }

  private fetchBrands(typeId: number): void {
    this.isBrandsLoading = true;
    this.vehicleService
      .getBrandsByType(typeId, { length: 1000 })
      .pipe(
        take(1),
        finalize(() => (this.isBrandsLoading = false)),
      )
      .subscribe((response: ApiResponse) => {
        if (response.data) {
          this.brandOptions = response.data;
        }
      });
  }

  private fetchModels(brandId: number): void {
    this.isYearsLoading = true;
    this.vehicleService
      .getModelsByBrand(brandId)
      .pipe(
        take(1),
        finalize(() => (this.isYearsLoading = false)),
      )
      .subscribe((response: ApiResponse) => {
        if (response.data) {
          this.modelsOptions = response.data;
        }
      });
  }

  private fetchYears(modelId: number): void {
    this.vehicleService
      .getModelYears(modelId)
      .pipe(
        take(1),
        finalize(() => (this.isModelsLoading = false)),
      )
      .subscribe((response: ApiResponse) => {
        if (response.data) {
          this.yearsOptions = response.data.map((year: string) => ({
            value: year,
          }));
        }
      });
  }

  private handleVehicleFormVINResponse(vehicle: VehicleFromVIN): void {
    this.isVINNumberValid = true;
    const objectToPatch: any = {};
    if (vehicle.brand && vehicle.brand_id) {
      const brand: VehicleBrand = {
        id: vehicle.brand_id,
        name: vehicle.brand,
      };
      this.brandOptions.push(brand);
      Object.assign(objectToPatch, { make: brand });
    }
    if (vehicle.model && vehicle.model_id) {
      const model: VehicleModel = {
        id: vehicle.model_id,
        name: vehicle.model,
      };
      this.modelsOptions.push(model);
      Object.assign(objectToPatch, { model });
    }
    if (vehicle.year_number) {
      const year = vehicle.year_number + '';
      this.yearsOptions.push({ value: year });
      Object.assign(objectToPatch, { year });
    }
    Object.assign(objectToPatch, {
      cylinder: vehicle.cylinder,
      trim_id: vehicle.trim,
      drive_type: vehicle.drive_type,
      fuel_type: vehicle.fuel_type,
      is_turbo: vehicle.is_turbo,
      tonnage: vehicle.standard_specifications?.tonnage || 0,
      gross_vehicle_weight_rating: vehicle.standard_specifications?.gross_vehicle_weight_rating || null,
      engine_aspiration: vehicle.engine_aspiration || 'N/A',
      engine_size: vehicle.engine_size,
      msrp: vehicle.msrp
    });
    this.vehicleForm.patchValue(objectToPatch);
    this.vehicleForm.updateValueAndValidity();
  }

  resetVehicleData() {
    const objectToPatch = {
      msrp: 0,
      make: null,
      model: null,
      trim_id: null,
      drive_type: null,
      cylinder: null,
      tonnage: null,
      gross_vehicle_weight_rating: null,
      engine_aspiration: null,
      engine_size: null,
    };
    this.vehicleForm.patchValue(objectToPatch);
  }

  private handleEditedVehicle(vehicle: VehicleFromVIN): void {
    const mapedVehicle = this.vehicleLogicService.getVehicleToPatch(vehicle);
    if (mapedVehicle.make) {
      this.brandOptions.push(mapedVehicle.make);
    }
    if (mapedVehicle.model) {
      this.modelsOptions.push(mapedVehicle.model);
    }
    if (mapedVehicle.year) {
      this.yearsOptions.push({ value: mapedVehicle.year });
    }
    if (mapedVehicle.owner) {
      this.ownerOptions.push(mapedVehicle.owner);
    }
    this.vehicleForm.patchValue(mapedVehicle);
    this.vehicleForm.updateValueAndValidity();
  }
}
